import { authentication, logger, ShareModal, useCreateDirectLink } from '@ecosystem/newron-client-lib';
import React, { useCallback, useEffect, useState } from 'react';
import { AccessLevel, UserAccess } from '@utils/pageConfig/Types';
import { PreferenceApiService } from '@services/userPreferences/PreferencesApi.service';
import { PREFERENCE_TYPE } from '@utils/userPreferencesUtils/Types';
import { SharingUserGroup, UserToShare } from '@services/savedFilter/UseSharingConfig';
import { ErrorTagEnum, saveErrorLog } from '@utils/LogUtil';
import { AddPreferencePermissionMutateResponse } from '@utils/hook/userPreferences/UseAddPermissionPreferences';
import { GeneralViewState } from '@models/SavedViews';
import { UserGroupInfoPermission } from '@ecosystem/newron-client-collections/build/shared/types';
import { ShareUser, ShareUserGroup } from '@ecosystem/newron-client-lib/build/sharing/types';
import { NotificationDto, paramPreparationForNotificationApi, sendNotification } from '@services/Notification.service';
import { getImpersonatedClientUserInfo } from '@utils/impersonateUtils/ImpersonationUtil';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { RetailerClient } from '@models/RetailerClient';
import { getSelectedClientNameWithClientId } from '@components/multi-banner-retailer-selector/MultiBannerRetailerSelectorUtils';

export interface ShareGroupModalProps {
  isOpen: boolean;
  itemToShare: string;
  onClose: () => void;
  onSuccess: () => void;
  onShareError: (error: Error) => void;
  getUsers: () => Promise<UserToShare[]>;
  getShareUserGroups: () => Promise<SharingUserGroup[]>;
  namespace: string;
  selectedGroupingType: PREFERENCE_TYPE;
  addPreferencePermission: AddPreferencePermissionMutateResponse<any>;
  saveViewFilter: GeneralViewState;
  retailerClients: RetailerClient[];
}

export interface PreferencesSharedInfo {
  individualUsers: ShareUser[];
  userGroups: ShareUserGroup[];
}

export const ShareGroupModal: React.FC<ShareGroupModalProps> = ({
  isOpen,
  itemToShare,
  onClose,
  onSuccess,
  onShareError,
  getUsers,
  getShareUserGroups,
  namespace,
  selectedGroupingType,
  addPreferencePermission,
  saveViewFilter,
  retailerClients,
}) => {
  const owner = authentication.getUser();
  const pagesConfig = usePageConfig();
  const [currentlySharedUsers, setCurrentlySharedUsers] = useState<ShareUser[]>([]);
  const [currentlySharedUserGroups, setCurrentlySharedUserGroups] = useState<ShareUserGroup[]>([]);
  const [availableUserGroupsState, setAvailableUserGroupsState] = useState<SharingUserGroup[]>([]);
  const { createLink } = useCreateDirectLink();
  const createViewState = useCallback(() => saveViewFilter, [saveViewFilter]);

  const currentImpersonatedUser = getImpersonatedClientUserInfo();
  //If currentImpersonatedUser is empty, it will use back owner info who has login
  const ownerClientId = currentImpersonatedUser?.clientId ?? owner?.clientId ?? 0;
  const ownerClientName = getSelectedClientNameWithClientId(retailerClients, pagesConfig.clientId);

  /**
   * Function to get the currently shared users
   */
  const updateCurrentlySharedUsers = useCallback(
    async (availableUserGroups: SharingUserGroup[]): Promise<PreferencesSharedInfo> => {
      const userAccess: UserAccess[] = await PreferenceApiService.getPreferencesPermissions(
        itemToShare,
        selectedGroupingType,
        namespace,
      );

      const individualUsers: ShareUser[] = userAccess
        .filter((user) => user.userInfo)
        .map((user) => {
          const { email, familyName, givenName, userId } = user.userInfo!;
          return {
            accessLevel: user.accessLevel,
            email: email,
            clientId: ownerClientId ?? 0,
            familyName: familyName,
            givenName: givenName,
            userId: userId,
          } as ShareUser;
        });
      //Set Individual User
      setCurrentlySharedUsers(individualUsers);

      const userGroups: ShareUserGroup[] = userAccess
        .filter((user) => user.userGroupInfo)
        .map((user) => {
          const userGroup = availableUserGroups?.find((group) => group.userGroupId === user.userGroupInfo!.userGroupId);

          return {
            accessLevel: user.accessLevel,
            ...userGroup,
          } as ShareUserGroup;
        });

      //Set Shared User Groups
      setCurrentlySharedUserGroups(userGroups);

      return { individualUsers: individualUsers, userGroups: userGroups } as PreferencesSharedInfo;
    },
    [itemToShare, owner?.userId],
  );

  //call preferences to get the list of currently shared users
  useEffect(() => {
    const fetchData = async () => {
      if (!isOpen) {
        // Clear state only when modal is closed, also to prevent it load previous preference info to selected module
        setCurrentlySharedUsers([]);
        setCurrentlySharedUserGroups([]);
        return;
      }

      //Get all available user groups
      const allUserGroups = await getShareUserGroups();
      const impersonatedGroupIds = currentImpersonatedUser?.clientUserGroupIds ?? [];

      //User group listing from share group modal has to filter with impersonated user group
      const filterAvailableUserGroups = allUserGroups.filter((userGroup) =>
        impersonatedGroupIds.includes(userGroup.userGroupId),
      );
      setAvailableUserGroupsState(filterAvailableUserGroups);
      //Setting Shared User/Group has to pass in all available user group
      await updateCurrentlySharedUsers(allUserGroups);
    };

    fetchData();
  }, [itemToShare, isOpen, owner?.userId, getShareUserGroups, updateCurrentlySharedUsers]);

  /**
   * Function to get the available users you are allowed to share with
   */
  const getAvailableUsers = useCallback(async () => {
    const users = await getUsers();
    return users.map((user) => ({
      userId: user.userId,
      givenName: user.givenName,
      familyName: user.familyName,
      email: user.email,
      clientId: ownerClientId ?? 0,
    }));
  }, [getUsers, ownerClientId ?? 0]);

  /**
   * Function to get the available user group you are allowed to share with
   */
  const getAvailableUsersGroup = useCallback(async () => {
    return availableUserGroupsState;
  }, [availableUserGroupsState]);

  const onCustomSharedSuccess = useCallback(
    (users: ShareUser[]) => {
      if (onSuccess) {
        onSuccess();
      }
      logger.info(`Successfully shared Grouping. Total viewers: ${users.length}`);
    },
    [onSuccess],
  );

  /**
   * Function to PATCH users to the permissions endpoint
   */
  const onSave = useCallback(
    async (users: ShareUser[], userGroups?: UserGroupInfoPermission[]) => {
      //Concat users and user groups as permissionBody
      const permissionsBody = [
        ...users.map((user) => ({
          userId: user.userId,
          access: user.accessLevel ?? AccessLevel.Viewer,
        })),
        ...(userGroups?.map((userGroup) => ({
          userGroupId: userGroup.userGroupId,
          access: userGroup.accessLevel ?? AccessLevel.Viewer,
        })) ?? []),
      ];

      onCustomSharedSuccess(users);

      addPreferencePermission.mutate({
        key: itemToShare,
        permissionBody: permissionsBody,
      });

      const availableUserGroups = await getShareUserGroups();
      const preferenceSharedInfo = await updateCurrentlySharedUsers(availableUserGroups);
      //Get Copy link base on request
      const { url: copyLinkUrl } = await createLink(createViewState, { dataType: 'static', purpose: 'copy-link' });
      const params: NotificationDto = paramPreparationForNotificationApi(
        itemToShare,
        users,
        preferenceSharedInfo.individualUsers,
        copyLinkUrl,
      );

      //Send notification (Notification to Bell / email are handle by platform)
      //And recipients cannot be empty
      if (params.sharedEntity.recipients.length > 0) {
        await sendNotification(params);
      }
    },
    [itemToShare],
  );

  const handleGetUsersError = useCallback(
    (err: Error) => {
      saveErrorLog(err, `Get users to share grouping`, {
        category: ErrorTagEnum.ApiError,
      });
    },
    [owner?.userId],
  );

  const handleShareError = useCallback(
    (err: Error) => {
      saveErrorLog(err, 'Get users to share grouping', {
        category: ErrorTagEnum.ApiError,
      });
      onShareError?.(err);
    },
    [onShareError],
  );

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <ShareModal
      //key to force shareModal to remount
      key={`${itemToShare}-${isOpen}`}
      itemName={itemToShare}
      getAvailableUsers={getAvailableUsers}
      getAvailableUserGroups={getAvailableUsersGroup}
      users={currentlySharedUsers}
      userGroups={currentlySharedUserGroups}
      onClose={handleClose}
      onShareError={handleShareError}
      onGetUsersError={handleGetUsersError}
      onSave={onSave}
      isOpen={isOpen}
      onSuccess={onSuccess}
      itemType="Grouping"
      sharableClients={[{ clientId: ownerClientId, clientName: ownerClientName }]}
    />
  );
};
